import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { prepare } from './helpers';
import { Params } from './types';
import { getSignalsObj } from '../../common/utils/signal-set-utils';
import { getLabel } from '../../common/utils/signal-set-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData?: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  params: Params;
  context: ts.types.signalSet.SignalSetAnalysisContext;
  title: string;
  subtitle?: string;
  setExternalEmbed: (_json: ts.types.analysis.EmbedChartData) => void;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  file,
  fullScreen,
  context,
  params,
  title,
  subtitle,
  setExternalEmbed,
}: ChartProps) => {
  const [data, setData] = React.useState<Record<string, ts.types.widgets.TableData>>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [displayTabs, setDisplayTabs] = React.useState<string[]>();
  const [selectedTab, setSelectedTab] = React.useState<string>();

  const resources = useSelector((state) => state.resources);
  const signalsObj = React.useMemo(() => getSignalsObj(context), [context]);

  const timeSeries = useSelector((state) => state.resources.time_series);
  const selectedTimeSeries = React.useMemo(() => {
    return params.analysis_time_series_ids.map((tid) => timeSeries.find((ts) => ts.id == tid));
  }, []);

  const setTabs = (lData: ts.types.widgets.TableData[number]) => {
    const tabs = new Set<string>();
    Object.keys(lData).forEach((key) => {
      if (key != 'date') {
        const tab = key.split('|')[1];
        if (tab) tabs.add(tab);
      }
    });
    const tabsArr = Array.from(tabs) as string[];
    setSelectedTab(tabsArr?.[0]);
    setDisplayTabs(tabsArr);
  };

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
        },
      });
      if (response) {
        setTabs(response.data[0]);
        setData(prepare(response.data, resources, signalsObj, selectedTimeSeries));
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const getTabs = () => {
    if (displayTabs) {
      return (
        <mui.core.Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedTab}
          onChange={(e, newTab) => setSelectedTab(newTab)}
          aria-label="Return Horizon Tabs"
        >
          {displayTabs.map((tab) => (
            <mui.core.Tab key={tab} label={getLabel(tab, context.frequency)} value={tab} />
          ))}
        </mui.core.Tabs>
      );
    }
    return null;
  };

  const inSameScale = React.useMemo(() => {
    if (!data) return [];
    return _.without(
      Object.keys(data?.[selectedTab]?.[0]),
      ...selectedTimeSeries.map(helpers.resourcesUtils.getResourceLabel)
    );
  }, [data, selectedTab, selectedTimeSeries]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.MultiScaleLineChart
          id={id}
          data={data?.[selectedTab]}
          fullScreen={fullScreen}
          readOnly={readOnly}
          numberFormatLeft="#.##%"
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
          exportSubTitle={subtitle}
          setExternalEmbed={setExternalEmbed}
          inSameScale={inSameScale}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
